import { Form, Formik, type FormikHelpers } from 'formik';
import { Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AUTH_TOKEN_NAME } from '../common/constants';
import CustomTextInput from '../components/Form/custom-text-input';
import { extractErrorsFromApi } from '../common/helpers';
import { login } from './auth.slice';
import logo from '../assets/logo.webp';
import RenderErrorBag from '../components/render-error-bag';
import { useAppDispatch } from '../app/hooks/hook';
import { useAuth } from '../app/hooks/useAuth';
import { useLoginUserMutation } from './auth.api-slice';
import { UserLoginSchema } from './auth.schema';
import { useTranslation } from 'react-i18next';

export interface UserLogin {
  email: string;
  password: string;
}

const initialUserLogin: UserLogin = {
  email: '',
  password: ''
};

const UserLoginForm: React.FC = () => {
  const [loginUser, { data, isSuccess, isLoading, isError, error }] = useLoginUserMutation();
  const [errorBag, setErrorBag] = useState<string[]>([]);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const auth = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    if (auth.user != null) {
      navigate('/admin/dashboard');
    }

    if (isSuccess && data?.jwtToken != null) {
      navigate('/admin/dashboard');
      dispatch(login({ token: data.jwtToken, user: data.user, loggedIn: true }));
      sessionStorage.setItem(AUTH_TOKEN_NAME, data?.jwtToken);
    }

    if (isError && error != null) {
      setErrorBag(extractErrorsFromApi(error));
    }
  }, [auth, data, dispatch, error, isError, isSuccess, navigate]);

  const onUserLogin = async (values: UserLogin, actions: FormikHelpers<UserLogin>): Promise<any> => {
    await loginUser({ email: values.email, password: values.password });
    actions.resetForm();
  };
  return (
    <div className="mx-auto h-[100vh] max-w-2xl space-y-4 text-center py-2 flex flex-col justify-center">
      <div className="flex flex-col bg-gray-50 rounded-md py-4 px-6">
        <div className="w-full text-center">
          <img src={logo} alt="Logo" className="px-32 inline h-[10vh]" />
        </div>
        <span className="w-full font-bold text-3xl mb-2 text-center">{t('auth.login.title')}</span>
        <div className="w-full">
          {error != null && errorBag.length > 0 && <RenderErrorBag errorBag={errorBag} />}
          <Formik initialValues={initialUserLogin} validationSchema={UserLoginSchema} onSubmit={onUserLogin}>
            {({ isSubmitting }) => (
              <Form className="w-full space-y-4 flex flex-col items-start px-2" autoComplete="off">
                <CustomTextInput
                  type="text"
                  label="Email"
                  name="email"
                  data-cy="email"
                  divClass="w-full flex flex-col"
                  labelClass="w-full text-left text-gray-600"
                  inputClass="w-full my-1 px-4 py-1 rounded-md"
                />
                <CustomTextInput
                  type="password"
                  label="Password"
                  name="password"
                  data-cy="password"
                  divClass="w-full flex flex-col"
                  labelClass="w-full text-left text-gray-600"
                  inputClass="w-full my-1 px-4 py-1 rounded-md"
                />
                <div className="w-full flex flex-col space-y-4 mt-16">
                  <button
                    type="submit"
                    disabled={isLoading}
                    data-cy="login-btn"
                    className={`w-full text-white px-5 py-2 rounded-lg bg-[#1D8A43] disabled:opacity-20`}
                  >
                    {t('auth.login.submitBtnLabel')}
                  </button>
                  <Link to="/register">
                    <button data-cy="register-page-btn" className="w-full text-black px-5 py-2 rounded-lg bg-white">
                      {t('auth.login.registerBtnLabel')}
                    </button>
                  </Link>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};

export default UserLoginForm;
